import axios from "axios";
import { API } from "@/utils/api/api_paths";
import { getUTMFromURLOrCookie } from "@/utils/functions";
import { useMainStore } from "@/stores/main";
import { useSupplementsStore } from "@/stores/supplements";
import { useQuestionsStore } from "@/stores/questions";
import { useRoute } from "vue-router";
import { LifestyleQuestionKey, type Question } from "@/types/questions";

export function useCrm() {
  const questionStore = useQuestionsStore();
  const supplementsStore = useSupplementsStore();
  const mainStore = useMainStore();
  const qStore = useQuestionsStore();
  const route = useRoute();

  const createContact = (): Promise<void | false> => {
    const body = {
      firstName: qStore.lifestyleStore.firstName,
      lastName: qStore.lifestyleStore.lastName,
      email: qStore.lifestyleStore.email,
      formula: JSON.stringify(supplementsStore.supplementsFormula),
      locale: mainStore.locale,
      consent: qStore.marketingConsentSelected,
      answers: JSON.stringify(questionStore.allAnswers),
      answersLabels: questionStore.readableAnswers,
      utmParameters: getUTMFromURLOrCookie(route.query),
      preferred_language: mainStore.locale,
      go___ip_address: mainStore.ipLocCountry.ip,
      go___ip_country: mainStore.ipLocCountry.name,
      formula_draft_uuid: supplementsStore.formulaDraftUuid,
    };

    return axios
      .post(API.CONTACT, body, { withCredentials: true })
      .then(() => saveHubspotContactEmail(qStore.lifestyleStore.email))
      .catch((err) => {
        console.error("Error on creating contact, ", err);
        return false;
      });
  };

  const updateContact = (hubspotEmail: string) => {
    const consent = qStore.marketingConsentSelected;
    const customerInfo = {
      firstName: qStore.lifestyleStore.firstName,
      lastName: qStore.lifestyleStore.lastName,
      email: qStore.lifestyleStore.email,
      consent,
      answers: JSON.stringify(questionStore.allAnswers),
      answersLabels: questionStore.readableAnswers,
      go_checkout_started: true,
      preferred_language: mainStore.locale,
      go___ip_address: mainStore.ipLocCountry.ip,
      go___ip_country: mainStore.ipLocCountry.name,
      formula_draft_uuid: supplementsStore.formulaDraftUuid,
      formula: JSON.stringify(supplementsStore.supplementsFormula),
    };
    return axios
      .put(`${API.CONTACT}/${hubspotEmail}`, { ...customerInfo })
      .then(() => saveHubspotContactEmail(qStore.lifestyleStore.email))
      .catch((err) => {
        console.error("Error on updating contact, ", err);
        return false;
      });
  };

  const updateContactWithSurveyQuestion = (hubspotEmail: string, val: string) => {
    const extraInfo = { how_did_you_hear_us_from_go_survey: val };

    return axios
      .put(`${API.CONTACT}/${hubspotEmail}`, { ...extraInfo })
      .then((response) => [null, response])
      .catch((err) => {
        console.error(`Error updating Hubspot Contact with 'How you heard about Bioniq' question, ${err}`);
        return [err, null];
      });
  };

  const getContactById = (id: string) => {
    return axios
      .get(`${API.CONTACT}/${id}`)
      .then((response) => {
        return {
          name: `${response.data.firstname} ${response.data.lastname}`,
          email: response.data.email,
          formula: response.data.formula_content ? JSON.parse(response.data.formula_content) : "",
          phoneNumber: response.data.phone || "",
          marketingConsent: response.data.ab_marketing_consent === "true",
          smsConsent: response.data.agree_to_sms === "true",
          answers: response.data.questionnaire_answers ? JSON.parse(response.data.questionnaire_answers) : "",
          goIpCountry: response.data.go___ip_country,
          goIpAddress: response.data.go___ip_address,
          formulaDraftUuid: response.data.formula_draft_uuid,
        };
      })
      .catch((err) => {
        console.error("Error on the GET contact request, ", err);
        return false;
      });
  };

  const loadDataFromContact = async (toRoute: any) => {
    const contactId = toRoute.query.contactId as string;
    const contact: any = await getContactById(contactId);
    if (!contact || !contact.formula) return false;

    localStorage.clear();

    const qStore = useQuestionsStore();

    supplementsStore.setFormula(contact.formula);
    if (contact.answers) qStore.updateAnswersForAllStores(contact.answers);
    // qStore.lifestyleStore.updateName(contact.name);
    // qStore.lifestyleStore.updateEmail(contact.email);
    // qStore.marketingConsentSelected = contact.marketingConsent;
    // qStore.lifestyleStore.smsConsentSelected = contact.smsConsent;
    saveHubspotContactEmail(contact.email);
    return true;
  };

  const saveHubspotContactEmail = (email: string) => {
    const mainStore = useMainStore();
    mainStore.setHubspotContactEmail(email);
  };

  const shouldLoadContact = (toRoute: any) => {
    return !!toRoute.query.contactId;
  };

  const isConsentSelectedIfEmailQuestion = (activeQuestion: Question) => {
    const questStore = useQuestionsStore();
    if (activeQuestion.key === LifestyleQuestionKey.EMAIL) return questStore.marketingConsentSelected;
    return true;
  };

  return {
    createContact,
    updateContact,
    loadDataFromContact,
    saveHubspotContactEmail,
    shouldLoadContact,
    isConsentSelectedIfEmailQuestion,
    updateContactWithSurveyQuestion,
  };
}
